import React, { useState, useEffect } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { useQuery } from '@apollo/client';
import { Agent, AgentType } from '../../../types';
import { Button, Select, LoadingSpinner } from '../../common';
import { Modal } from '../../common/Modal';
import { useKeycloakAuth } from '../../../contexts/KeycloakAuthProvider';
import { useAdminTranslation } from '../../../modules/i18n';
import { useKnowledgeGraphApi, useAgentApi } from '../../../contexts/ApiContext';
import { useNotification } from '../../Notification/NotificationContext';
import { useKGImportProgressContext } from '../../../contexts/KGImportProgressContext';
import KGImportProgressTracker from '../KnowledgeGraphBuild/KGImportProgressTracker';
import KGValidationTable from '../../shared/KnowledgeGraph/Validation/KGValidationTable';
import WorkflowEditorOverlay from './WorkflowEditorOverlay';
import { getDefaultWorkflow, getDefaultDirectQAWorkflow, type SupportedLanguage } from '../Workflow/WorkflowEditor/defaultWorkflow';
import { useWorkflowMetadata } from '../../../stores/workflow';
import { GET_AGENTS, GET_FOLDERS } from '../../../graphql/queries';

interface AgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (agent: Partial<Agent>) => void;
  editingAgent?: Agent | null;
  onAgentCreated?: () => void;
}

// Tool selection interface
 
interface SelectedTool extends Record<string, any> {
  toolId: string;
  isActive: boolean;
  config: object | null;
}

/**
 * Unified Agent Modal - Supports two agent types:
 *
 * KG_RAG CREATE MODE (4 steps):
 * 0: Basic Info (name, language, agent type)
 * 1: KG Config (file upload)
 * 2: Validation (validate KG files)
 * 3: Workflow (workflow editor with all prompts in nodes/metadata)
 *
 * DIRECT_QA CREATE MODE (2 steps):
 * 0: Basic Info (name, language, agent type, folder selection)
 * 1: Workflow (simple 3-node workflow editor with prompts in nodes/metadata)
 *
 * EDIT MODE (2 steps):
 * 0: Basic Info (name, language)
 * 1: Workflow (both KG_RAG and DIRECT_QA use workflow editor)
 */
const AgentModal: React.FC<AgentModalProps> = ({ isOpen, onClose, onSave: _onSave, editingAgent, onAgentCreated }) => {
  const { t } = useAdminTranslation();
  const { startTracking, dismissNotification, setSuppressGlobalIndicator, activeJobId: contextJobId, isPolling } = useKGImportProgressContext();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Agent type selection
  const [agentType, setAgentType] = useState<AgentType>('KG_RAG');

  // Get user from Keycloak context
  const { user } = useKeycloakAuth();
  const agentApi = useAgentApi();

  // Get KG API service and notification
  const kgApi = useKnowledgeGraphApi();
  const { addNotification } = useNotification();

  // Fetch folders for Direct Q&A agent type
  const { data: foldersData, loading: foldersLoading } = useQuery(GET_FOLDERS, {
    variables: { userId: user?.id || '' },
    skip: !user?.id || !isOpen
  });

  // Form data
  const [formData, setFormData] = useState<Partial<Agent>>({
    name: '',
    languageId: 'vi',
    agentFolders: [],
    tools: []
  });

  // Selected folder IDs for Direct Q&A
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);

  // Tool selection state
  const [selectedTools, setSelectedTools] = useState<SelectedTool[]>([]);

  // KG file upload state (single kg.pkl)
  const [kgFile, setKgFile] = useState<File | null>(null);

  // KG Validation state
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<import('../../../types/knowledgeGraph').KGImportValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [validationProgress, setValidationProgress] = useState<{
    phase: 'uploading' | 'validating';
    progress: number;
  } | null>(null);

  // KG Import progress tracking
  const [showImportProgress, setShowImportProgress] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [isImportComplete, setIsImportComplete] = useState(false);

  // Suppress global indicator when import progress modal is open
  useEffect(() => {
    setSuppressGlobalIndicator(showImportProgress);
    return () => setSuppressGlobalIndicator(false);
  }, [showImportProgress, setSuppressGlobalIndicator]);

  // Workflow state
  const [showWorkflowEditor, setShowWorkflowEditor] = useState(false);
  const [workflowDefinition, setWorkflowDefinition] = useState<Record<string, unknown> | null>(null);
  const { workflowMetadata, setWorkflowMetadata } = useWorkflowMetadata();

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setShowImportProgress(false);
      setImportJobId(null);
      setKgFile(null);
      setValidationResult(null);
      setValidationError('');
      setError('');
      setSaveSuccess(false);
      setShowWorkflowEditor(false);
      setWorkflowDefinition(null);
      setAgentType('KG_RAG');
      setSelectedFolderIds([]);
    }
  }, [isOpen]);

  // Reconnect to in-progress KG import after page reload
  useEffect(() => {
    if (isOpen && contextJobId && isPolling && !importJobId) {
      setImportJobId(contextJobId);
      setShowImportProgress(true);
    }
  }, [isOpen, contextJobId, isPolling, importJobId]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (showImportProgress || importJobId) return;

    if (editingAgent) {
      // Load existing agent data
      setAgentType(editingAgent.agentType || 'KG_RAG');

      if (editingAgent.workflow?.definition) {
        setWorkflowDefinition(editingAgent.workflow.definition as Record<string, unknown>);
      }

      setFormData({
        name: editingAgent.name,
        languageId: editingAgent.languageId,
        agentFolders: editingAgent.agentFolders || [],
        tools: editingAgent.tools || []
      });

      setSelectedFolderIds(editingAgent.agentFolders?.map((af) => af.folderId) || []);

      setSelectedTools(
        editingAgent.tools?.map((tool) => ({
          toolId: tool.toolId,
          isActive: tool.isActive,
          config: tool.config ?? null
        })) || []
      );
    } else {
      // Set default values for new agent
      const defaultLang: SupportedLanguage = 'vi';
      const defaultWorkflow = getDefaultWorkflow(defaultLang);

      setFormData({
        name: '',
        languageId: defaultLang,
        agentFolders: [],
        tools: []
      });
      setSelectedTools([]);
      setSelectedFolderIds([]);

      // Load default workflow and populate metadata (display messages only)
      setWorkflowDefinition(defaultWorkflow as unknown as Record<string, unknown>);
      setWorkflowMetadata({
        welcomeMessage: defaultWorkflow.metadata.welcomeMessage || '',
        errorMessage: defaultWorkflow.metadata.errorMessage || ''
      });
    }
  }, [isOpen, editingAgent, showImportProgress, importJobId, setWorkflowMetadata]);

  // Determine mode
  const isEditMode = !!editingAgent;
  const isDirectQa = agentType === 'DIRECT_QA';

  // Fetch existing agents for name uniqueness check (per-user scope)
  // Apollo cache is shared with useAgents() hook — no duplicate network call.
  const { data: existingAgentsData } = useQuery(GET_AGENTS, { skip: !isOpen });

  // Inline name-uniqueness validation (debounced, case-insensitive, trimmed)
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    const raw = formData.name ?? '';
    const normalized = raw.trim().toLowerCase();

    if (!normalized) {
      setNameError(null);
      return;
    }

    const agents = existingAgentsData?.findManyAgents;
    if (!agents) {
      setNameError(null);
      return;
    }

    const handle = setTimeout(() => {
      const duplicate = agents.some(
        (a: { id: string; name: string }) => a.id !== editingAgent?.id && a.name.trim().toLowerCase() === normalized
      );
      setNameError(duplicate ? t('content.agents.modal.errors.nameExists') : null);
    }, 300);

    return () => clearTimeout(handle);
  }, [formData.name, existingAgentsData, editingAgent?.id, t]);

  // Handle language change - reload workflow with language-specific prompts
  const handleLanguageChange = (languageId: string) => {
    setFormData({ ...formData, languageId });

    // Reload workflow with new language if not editing existing agent
    if (!editingAgent) {
      const langCode = (languageId === 'en' ? 'en' : 'vi') as SupportedLanguage;
      const newWorkflow = isDirectQa ? getDefaultDirectQAWorkflow(langCode) : getDefaultWorkflow(langCode);
      setWorkflowDefinition(newWorkflow as unknown as Record<string, unknown>);

      const metadata = newWorkflow.metadata;
      setWorkflowMetadata({
        welcomeMessage: metadata.welcomeMessage || '',
        errorMessage: metadata.errorMessage || ''
      });
    }
  };

  // Step navigation
  const getTotalSteps = () => {
    if (isEditMode) {
      return 1; // Basic Info + Workflow/Prompts
    }
    if (isDirectQa) {
      return 1; // Step 0: Basic Info + Folders, Step 1: Prompts
    }
    return 3; // KG_RAG: Basic Info, KG Upload, Validation, Workflow
  };

  const nextStep = async () => {
    const maxStep = getTotalSteps();

    // In edit mode or Direct Q&A, simple step progression
    if (isEditMode || isDirectQa) {
      if (currentStep < maxStep) {
        setCurrentStep(currentStep + 1);
      }
      return;
    }

    // KG_RAG CREATE MODE: If moving from Step 1 (KG Upload) to Step 2 (Validation), trigger validation
    if (currentStep === 1) {
      setCurrentStep(2);

      if (kgFile) {
        setIsValidating(true);
        setValidationError('');
        setValidationResult(null);
        setValidationProgress(null);

        try {
          const result = await kgApi.validateImportChunked(kgFile, (phase, progress) => {
            setValidationProgress({ phase, progress });
          });

          setValidationResult(result);

          if (result.overall_status !== 'passed') {
            setValidationError(t('content.agents.modal.validation.failedMessage'));
          }
        } catch (error: any) {
          const errorMsg = error.message || t('content.agents.modal.errors.kgValidationFailed');
          setValidationError(errorMsg);
          addNotification({
            type: 'error',
            title: t('content.agents.modal.validation.errorTitle'),
            message: errorMsg,
            duration: 5000
          });
        } finally {
          setIsValidating(false);
          setValidationProgress(null);
        }
      }
    } else if (currentStep < maxStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Step validation
  const isStepValid = () => {
    // Step 0: Basic info
    if (currentStep === 0) {
      const hasName = formData.name?.trim() !== '';
      const hasLanguage = formData.languageId !== '';
      const nameOk = hasName && !nameError;
      // DIRECT_QA agents require at least one folder
      if (isDirectQa) {
        return nameOk && hasLanguage && selectedFolderIds.length > 0;
      }
      return nameOk && hasLanguage;
    }

    // EDIT MODE
    if (isEditMode) {
      if (currentStep === 1) {
        return workflowDefinition !== null;
      }
      return true;
    }

    // DIRECT_QA CREATE MODE
    if (isDirectQa) {
      if (currentStep === 1) {
        return workflowDefinition !== null;
      }
      return true;
    }

    // KG_RAG CREATE MODE: steps 1, 2, 3
    if (currentStep === 1) {
      return kgFile !== null;
    }
    if (currentStep === 2) {
      return validationResult !== null && validationResult.overall_status === 'passed' && !isValidating;
    }
    if (currentStep === 3) {
      return workflowDefinition !== null;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // EDIT MODE: Update agent via PUT endpoint
      if (isEditMode && editingAgent) {
        const finalWorkflowDef = workflowDefinition
          ? {
              ...workflowDefinition,
              metadata: {
                ...(workflowDefinition.metadata as Record<string, unknown> | undefined),
                welcomeMessage: workflowMetadata.welcomeMessage || '',
                errorMessage: workflowMetadata.errorMessage || ''
              }
            }
          : undefined;

        const updatePayload = {
          name: formData.name || editingAgent.name,
          agent_type: agentType,
          language_id: formData.languageId || 'vi',
          agent_folders: isDirectQa ? selectedFolderIds.map((id) => ({ folder_id: id })) : [],
          tools: isDirectQa
            ? []
            : selectedTools.map((t) => ({
                tool_id: t.toolId,
                is_active: t.isActive,
                config: t.config
              })),
          workflow_definition: finalWorkflowDef
        };

        await agentApi.updateAgent(editingAgent.id, updatePayload);

        addNotification({
          type: 'success',
          title: t('content.agents.modal.success.updateTitle'),
          message: t('content.agents.modal.success.updateMessage'),
          duration: 3000
        });
        onAgentCreated?.();
        onClose();
        return;
      }

      // CREATE MODE: Direct Q&A
      if (isDirectQa) {
        const finalDqaWorkflowDef = workflowDefinition
          ? {
              ...workflowDefinition,
              metadata: {
                ...(workflowDefinition.metadata as Record<string, unknown> | undefined),
                welcomeMessage: workflowMetadata.welcomeMessage || '',
                errorMessage: workflowMetadata.errorMessage || ''
              }
            }
          : undefined;

        const createPayload = {
          name: formData.name || '',
          agent_type: 'DIRECT_QA',
          language_id: formData.languageId || 'vi',
          agent_folders: selectedFolderIds.map((id) => ({ folder_id: id })),
          tools: [],
          workflow_definition: finalDqaWorkflowDef
        };

        const responseData = await agentApi.createAgent(createPayload);
        const responseDataTyped = responseData as { data?: { cached_token_count?: number } };
        const tokenCount = responseDataTyped?.data?.cached_token_count;
        const tokenInfo = tokenCount ? ` (${tokenCount.toLocaleString()} tokens cached)` : '';

        addNotification({
          type: 'success',
          title: t('content.agents.modal.success.createTitle'),
          message: t('content.agents.modal.success.createMessage') + tokenInfo,
          duration: 5000
        });
        onAgentCreated?.();
        onClose();
        return;
      }

      // CREATE MODE: KG_RAG (existing flow)
      if (!validationResult || validationResult.overall_status !== 'passed') {
        throw new Error(t('content.agents.modal.errors.kgValidationRequired'));
      }

      const finalWorkflowDefinition = workflowDefinition
        ? {
            ...workflowDefinition,
            metadata: {
              ...(workflowDefinition as any).metadata,
              welcomeMessage: workflowMetadata.welcomeMessage || '',
              errorMessage: workflowMetadata.errorMessage || ''
            }
          }
        : undefined;

      const agentConfig = {
        name: formData.name || '',
        languageId: formData.languageId || 'vi',
        tools: selectedTools,
        workflowDefinition: finalWorkflowDefinition
      };

      const response = await kgApi.executeImport(validationResult.validation_id, agentConfig);

      setImportJobId(response.job_id);
      setShowImportProgress(true);

      startTracking(response.job_id, {
        pollInterval: 3000,
        maxRetries: 3,
        notificationEnabled: false,
        onComplete: (data) => {
          if (data.status === 'completed') {
            setIsImportComplete(true);
          }
        },
        onError: (errorMsg) => {
          console.error('KG import failed:', errorMsg);
        }
      });
    } catch (error: any) {
      if (error.message?.includes('Unique constraint failed')) {
        setError(t('content.agents.modal.errors.nameExists'));
      } else {
        setError(error.message || t('content.agents.modal.errors.saveFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Agent type toggle component
  const renderAgentTypeToggle = () => {
    if (isEditMode) return null; // Cannot change type when editing

    return (
      <div className='mb-4'>
        <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
          {t('content.agents.agentType.label')} <span className='text-red-500'>*</span>
        </label>
        <div className='grid grid-cols-2 gap-3'>
          <button
            type='button'
            onClick={() => {
              setAgentType('KG_RAG');
              setCurrentStep(0);
              const langCode = (formData.languageId === 'en' ? 'en' : 'vi') as SupportedLanguage;
              const kgWorkflow = getDefaultWorkflow(langCode);
              setWorkflowDefinition(kgWorkflow as unknown as Record<string, unknown>);
              setWorkflowMetadata({
                welcomeMessage: kgWorkflow.metadata.welcomeMessage || '',
                errorMessage: kgWorkflow.metadata.errorMessage || ''
              });
            }}
            className={`flex items-center gap-3 rounded-lg border-2 p-4 transition-all ${
              agentType === 'KG_RAG'
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 dark:border-blue-400 dark:bg-blue-900/20 dark:ring-blue-800'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-border-dark dark:bg-dark-background-secondary dark:hover:border-border-dark'
            }`}
          >
            <CircleStackIcon
              className={`h-8 w-8 flex-shrink-0 ${agentType === 'KG_RAG' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
            />
            <div className='text-left'>
              <span
                className={`block text-sm font-semibold ${agentType === 'KG_RAG' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-dark-text-secondary'}`}
              >
                {t('content.agents.agentType.kgRag')}
              </span>
              <span className='mt-0.5 block text-xs text-gray-500 dark:text-dark-text-secondary'>
                {t('content.agents.agentType.kgRagDescription')}
              </span>
            </div>
          </button>

          <button
            type='button'
            onClick={() => {
              setAgentType('DIRECT_QA');
              setCurrentStep(0);
              const langCode = (formData.languageId === 'en' ? 'en' : 'vi') as SupportedLanguage;
              const dqaWorkflow = getDefaultDirectQAWorkflow(langCode);
              setWorkflowDefinition(dqaWorkflow as unknown as Record<string, unknown>);
              setWorkflowMetadata({
                welcomeMessage: dqaWorkflow.metadata.welcomeMessage || '',
                errorMessage: dqaWorkflow.metadata.errorMessage || ''
              });
            }}
            className={`flex items-center gap-3 rounded-lg border-2 p-4 transition-all ${
              agentType === 'DIRECT_QA'
                ? 'border-green-500 bg-green-50 ring-2 ring-green-200 dark:border-green-400 dark:bg-green-900/20 dark:ring-green-800'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-border-dark dark:bg-dark-background-secondary dark:hover:border-border-dark'
            }`}
          >
            <ChatBubbleLeftRightIcon
              className={`h-8 w-8 flex-shrink-0 ${agentType === 'DIRECT_QA' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}
            />
            <div className='text-left'>
              <span
                className={`block text-sm font-semibold ${agentType === 'DIRECT_QA' ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-dark-text-secondary'}`}
              >
                {t('content.agents.agentType.directQa')}
              </span>
              <span className='mt-0.5 block text-xs text-gray-500 dark:text-dark-text-secondary'>
                {t('content.agents.agentType.directQaDescription')}
              </span>
            </div>
          </button>
        </div>
      </div>
    );
  };

  // Render folder selection for Direct Q&A
  const renderFolderSelection = () => {
    const folders = (foldersData?.findManyFolders || []).filter((f: any) => !f.parentId);

    return (
      <div>
        <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
          {t('content.agents.modal.form.rootFolderLabel')}
        </label>
        {foldersLoading ? (
          <div className='flex items-center gap-2 py-2 text-sm text-gray-500'>
            <LoadingSpinner size='sm' />
            {t('content.agents.modal.form.foldersLoading')}
          </div>
        ) : folders.length === 0 ? (
          <p className='py-2 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.form.foldersEmpty')}</p>
        ) : (
          <div className='max-h-40 overflow-y-auto rounded-md border border-gray-300 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
            {folders.map((folder: any) => (
              <label
                key={folder.id}
                className='flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-border-dark dark:hover:bg-dark-background-hover'
              >
                <input
                  type='checkbox'
                  checked={selectedFolderIds.includes(folder.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedFolderIds([...selectedFolderIds, folder.id]);
                    } else {
                      setSelectedFolderIds(selectedFolderIds.filter((id) => id !== folder.id));
                    }
                  }}
                  className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-border-dark dark:bg-dark-background-tertiary'
                />
                <FolderIcon className='h-4 w-4 text-amber-500' />
                <span className='text-sm text-gray-900 dark:text-dark-text'>{folder.name}</span>
              </label>
            ))}
          </div>
        )}
        <p className='mt-1 text-xs text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.form.rootFolderNote')}</p>
      </div>
    );
  };

  // Render workflow step content (reusable for KG_RAG and Direct QA modes)
  const renderWorkflowStep = () => (
    <div className='space-y-4'>
      <label className='block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
        {t('content.agents.modal.form.workflowLabel')} <span className='text-red-500'>*</span>
      </label>

      <div
        className={`rounded-lg border-2 p-4 ${
          workflowDefinition
            ? 'border-purple-300 bg-purple-50 dark:border-purple-600 dark:bg-purple-900/20'
            : 'border-gray-300 bg-gray-50 dark:border-border-dark dark:bg-dark-background-secondary'
        }`}
      >
        {workflowDefinition ? (
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='rounded-full bg-purple-100 p-2 dark:bg-purple-900/30'>
                <CheckCircleIcon className='h-5 w-5 text-purple-600 dark:text-purple-400' />
              </div>
              <div>
                <p className='font-medium text-gray-900 dark:text-dark-text'>{t('content.agents.modal.workflow.configured')}</p>
                <p className='text-sm text-gray-500 dark:text-dark-text-secondary'>
                  {((workflowDefinition?.nodes as unknown[])?.length || 0)} {t('content.agents.modal.workflow.nodes')}
                </p>
              </div>
            </div>
            <Button
              type='button'
              variant='outline'
              onClick={() => setShowWorkflowEditor(true)}
              className='border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-400'
            >
              {t('content.agents.modal.workflow.edit')}
            </Button>
          </div>
        ) : (
          <div className='flex flex-col items-center gap-4 py-4'>
            <div className='rounded-full bg-gray-200 p-3 dark:bg-dark-background-tertiary'>
              <ShareIcon className='h-8 w-8 text-gray-400' />
            </div>
            <div className='text-center'>
              <p className='font-medium text-gray-900 dark:text-dark-text'>{t('content.agents.modal.workflow.notConfigured')}</p>
              <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.workflow.notConfiguredHint')}</p>
            </div>
            <Button type='button' onClick={() => setShowWorkflowEditor(true)} className='bg-purple-600 text-white hover:bg-purple-700'>
              <ShareIcon className='mr-2 h-4 w-4' />
              {t('content.agents.modal.workflow.openEditor')}
            </Button>
          </div>
        )}
      </div>

      <div className='rounded-lg border border-purple-200 bg-purple-50 p-3 dark:border-purple-800 dark:bg-purple-900/10'>
        <div className='flex items-center gap-2'>
          <InformationCircleIcon className='h-4 w-4 flex-shrink-0 text-purple-500' />
          <p className='text-xs text-purple-700 dark:text-purple-300'>{t('content.agents.modal.workflow.infoNote')}</p>
        </div>
      </div>
    </div>
  );

  // Render step content
  const renderStepContent = () => {
    // Step 0: Basic Info
    if (currentStep === 0) {
      return (
        <div className='space-y-4'>
          {/* Agent Type Toggle */}
          {renderAgentTypeToggle()}

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.agents.modal.form.nameLabel')} <span className='text-red-500'>{t('content.agents.modal.form.nameRequired')}</span>
            </label>
            <input
              type='text'
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full rounded-md border bg-white px-3 py-2 text-gray-900 transition-colors focus:outline-none focus:ring-2 dark:bg-dark-background-tertiary dark:text-dark-text ${
                nameError
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary focus:ring-primary dark:border-border-dark'
              }`}
              placeholder={t('content.agents.modal.form.namePlaceholder')}
              required
              aria-invalid={!!nameError}
              aria-describedby={nameError ? 'agent-name-error' : undefined}
            />
            {nameError && (
              <p id='agent-name-error' className='mt-1 text-xs text-red-600 dark:text-red-400'>
                {nameError}
              </p>
            )}
          </div>

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
              {t('content.agents.modal.form.languageLabel')} <span className='text-red-500'>{t('content.agents.modal.form.languageRequired')}</span>
            </label>
            <Select
              value={formData.languageId || 'vi'}
              onChange={handleLanguageChange}
              options={[
                { value: 'vi', label: t('content.agents.modal.languages.vietnamese') },
                { value: 'en', label: t('content.agents.modal.languages.english') }
              ]}
              placeholder={t('content.agents.modal.form.languagePlaceholder')}
            />
            <p className='mt-1 text-xs text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.form.languageNote')}</p>
          </div>

          {/* Folder selection for Direct Q&A */}
          {isDirectQa && renderFolderSelection()}
        </div>
      );
    }

    // EDIT MODE
    if (isEditMode) {
      if (currentStep === 1) {
        return renderWorkflowStep();
      }
      return null;
    }

    // DIRECT_QA CREATE MODE: Step 1 is Workflow
    if (isDirectQa && currentStep === 1) {
      return renderWorkflowStep();
    }

    // KG_RAG CREATE MODE: Steps 1, 2, 3
    switch (currentStep) {
      case 1:
        // Step 1: KG Config - File Upload
        return (
          <div className='space-y-4'>
            <div className='space-y-4'>
              <h4 className='flex items-center text-sm font-medium text-gray-900 dark:text-dark-text'>
                <DocumentIcon className='mr-2 h-5 w-5 text-primary' />
                {t('content.agents.modal.kgSource.import.uploadFiles')}
              </h4>

              <div className='relative rounded-lg border-2 border-dashed border-gray-300 bg-white p-6 transition-colors hover:border-primary hover:bg-primary/5 dark:border-border-dark dark:bg-dark-background-secondary dark:hover:bg-primary/10'>
                <input
                  type='file'
                  accept='.pkl'
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setKgFile(file);
                    setValidationResult(null);
                  }}
                  className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
                  id='kg-pickle-upload'
                />
                <label htmlFor='kg-pickle-upload' className='flex cursor-pointer flex-col items-center gap-3'>
                  {!kgFile ? (
                    <>
                      <div className='rounded-full bg-primary/10 p-3 dark:bg-primary-light/30'>
                        <svg className='h-8 w-8 text-primary dark:text-primary-light' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                          />
                        </svg>
                      </div>
                      <div className='text-center'>
                        <p className='text-sm font-medium text-gray-900 dark:text-dark-text'>
                          kg.pkl <span className='text-red-500'>*</span>
                        </p>
                        <p className='mt-1 text-xs text-gray-500 dark:text-dark-text-secondary'>{t('content.agents.modal.kgSource.import.selectFile')}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='rounded-full bg-green-100 p-3 dark:bg-green-900/30'>
                        <svg className='h-8 w-8 text-green-600 dark:text-green-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                        </svg>
                      </div>
                      <div className='text-center'>
                        <p className='text-sm font-medium text-gray-900 dark:text-dark-text'>kg.pkl</p>
                        <p className='mt-1 text-xs text-green-600 dark:text-green-400'>{kgFile.name}</p>
                      </div>
                    </>
                  )}
                </label>
              </div>

              <div className='rounded-lg border border-primary/30 bg-primary/10 p-3 dark:border-primary/50 dark:bg-primary/20'>
                <div className='flex items-center'>
                  <InformationCircleIcon className='h-4 w-4 flex-shrink-0 text-primary' />
                  <p className='ml-2 text-xs text-primary dark:text-dark-text-secondary'>{t('content.agents.modal.validation.uploadNote')}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        // Step 2: Validation
        if (isValidating) {
          return (
            <div className='space-y-4'>
              <div className='rounded-lg border-2 border-primary/30 bg-primary/10 p-6 text-center dark:border-primary/50 dark:bg-primary/20'>
                <div className='flex flex-col items-center'>
                  <LoadingSpinner size='lg' />
                  <h4 className='mt-4 text-lg font-medium text-gray-900 dark:text-dark-text'>
                    {t('content.agents.modal.validation.validatingTitle')}
                  </h4>
                  <p className='mt-2 text-sm text-gray-700 dark:text-dark-text-secondary'>{t('content.agents.modal.validation.validatingMessage')}</p>

                  {validationProgress && (
                    <div className='mt-6 w-full max-w-md'>
                      <div className='space-y-2'>
                        <div className='flex items-center justify-between text-sm'>
                          <span className='font-medium text-primary dark:text-sky-300'>
                            {validationProgress.phase === 'uploading'
                              ? t('content.agents.modal.validation.uploadingPhase')
                              : t('content.agents.modal.validation.validatingPhase')}
                          </span>
                          <span className='font-semibold text-primary dark:text-sky-300'>{Math.round(validationProgress.progress)}%</span>
                        </div>
                        <div className='h-3 overflow-hidden rounded-full bg-primary/20 dark:bg-primary/30'>
                          <div
                            className='h-full rounded-full bg-primary transition-all duration-300 dark:bg-primary-light'
                            style={{ width: `${validationProgress.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (validationError) {
          return (
            <div className='space-y-4'>
              <div className='rounded-lg border-2 border-red-200 bg-red-50 p-6 dark:border-red-700 dark:bg-red-900/20'>
                <div className='flex items-start'>
                  <ExclamationTriangleIcon className='mt-0.5 h-8 w-8 flex-shrink-0 text-red-500' />
                  <div className='ml-3'>
                    <h4 className='text-lg font-medium text-red-800 dark:text-red-200'>{t('content.agents.modal.validation.errorTitle')}</h4>
                    <p className='mt-2 text-sm text-red-700 dark:text-red-300'>{validationError}</p>
                    <p className='mt-3 text-xs text-red-600 dark:text-red-400'>{t('content.agents.modal.validation.errorHint')}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (validationResult) {
          return (
            <div className='space-y-4'>
              <div className='rounded-lg border-2 border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20'>
                <div className='flex items-start'>
                  <CheckCircleIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-green-500' />
                  <div className='ml-3'>
                    <h4 className='text-sm font-medium text-green-800 dark:text-green-200'>{t('content.agents.modal.validation.reviewTitle')}</h4>
                    <p className='mt-1 text-sm text-green-700 dark:text-green-300'>{t('content.agents.modal.validation.reviewMessage')}</p>
                  </div>
                </div>
              </div>

              <KGValidationTable validationResult={validationResult} />
            </div>
          );
        }

        return (
          <div className='rounded-lg border-2 border-yellow-200 bg-yellow-50 p-6 text-center dark:border-yellow-700 dark:bg-yellow-900/10'>
            <ExclamationTriangleIcon className='mx-auto mb-3 h-12 w-12 text-yellow-500' />
            <p className='text-lg font-medium text-yellow-900 dark:text-yellow-100'>{t('content.agents.modal.validation.noValidationTitle')}</p>
            <p className='mt-2 text-sm text-yellow-700 dark:text-yellow-300'>{t('content.agents.modal.validation.noValidationMessage')}</p>
          </div>
        );

      case 3:
        // Step 3: Workflow (KG_RAG CREATE MODE only)
        return renderWorkflowStep();

      default:
        return null;
    }
  };

  // Render step indicator
  const renderStepIndicator = () => {
    const getStepLabel = (step: number) => {
      if (isEditMode) {
        switch (step) {
          case 0:
            return t('content.agents.modal.steps.basic');
          case 1:
            return t('content.agents.modal.steps.workflow');
          default:
            return '';
        }
      }
      if (isDirectQa) {
        switch (step) {
          case 0:
            return t('content.agents.modal.steps.basic');
          case 1:
            return t('content.agents.modal.steps.workflow');
          default:
            return '';
        }
      }
      // KG_RAG CREATE MODE
      switch (step) {
        case 0:
          return t('content.agents.modal.steps.basic');
        case 1:
          return t('content.agents.modal.steps.kgConfig');
        case 2:
          return t('content.agents.modal.steps.validation');
        case 3:
          return t('content.agents.modal.steps.workflow');
        default:
          return '';
      }
    };

    const totalSteps = getTotalSteps();
    const steps = Array.from({ length: totalSteps + 1 }, (_, i) => i);

    return (
      <div className='mb-6 flex justify-between'>
        {steps.map((step) => (
          <div key={step} className='flex flex-col items-center'>
            <div
              className={`mb-1 flex h-8 w-8 items-center justify-center rounded-full
                ${currentStep === step ? 'bg-primary text-white' : currentStep > step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 dark:bg-dark-background-tertiary dark:text-dark-text'}`}
            >
              {currentStep > step ? <CheckIcon className='h-5 w-5' /> : <span>{step + 1}</span>}
            </div>
            <span className='text-xs text-gray-600 dark:text-dark-text'>{getStepLabel(step)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render step navigation
  const renderStepNavigation = () => {
    const isBackDisabled = currentStep === 0;
    const totalSteps = getTotalSteps();

    return (
      <div className='flex justify-between'>
        <Button type='button' variant='outline' onClick={prevStep} disabled={isBackDisabled} className='flex items-center space-x-1'>
          <ArrowLeftIcon className='h-4 w-4' />
          <span>{t('content.agents.modal.navigation.back')}</span>
        </Button>

        {currentStep < totalSteps ? (
          <Button type='button' onClick={nextStep} disabled={!isStepValid()} className='flex items-center space-x-1'>
            <span>{t('content.agents.modal.navigation.continue')}</span>
            <ArrowRightIcon className='h-4 w-4' />
          </Button>
        ) : (
          <Button type='button' onClick={handleSubmit} disabled={isSubmitting} className='flex items-center space-x-1'>
            {isSubmitting ? (
              <>
                <ArrowPathIcon className='h-4 w-4 animate-spin' />
                <span>{t('content.agents.modal.navigation.processing')}</span>
              </>
            ) : saveSuccess ? (
              <>
                <CheckCircleIcon className='h-4 w-4' />
                <span>{t('content.agents.modal.navigation.created')}</span>
              </>
            ) : (
              <span>{editingAgent ? t('content.agents.modal.navigation.updateButton') : t('content.agents.modal.navigation.createButton')}</span>
            )}
          </Button>
        )}
      </div>
    );
  };

  if (!isOpen && !showImportProgress && !showWorkflowEditor) return null;

  return (
    <>
      {/* Main Agent Creation Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={editingAgent ? t('content.agents.modal.edit') : t('content.agents.modal.create')}
        size='lg'
        height='xl'
        footer={renderStepNavigation()}
      >
        {error && (
          <div className='mb-4 rounded-md bg-red-50 p-4'>
            <div className='text-sm text-red-700'>{error}</div>
          </div>
        )}

        <div className='space-y-6'>
          {renderStepIndicator()}
          {renderStepContent()}
        </div>
      </Modal>

      {/* KG Import Progress Modal */}
      {showImportProgress && importJobId && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowImportProgress(false);
            setImportJobId(null);
            setIsImportComplete(false);
          }}
          title={t('content.agents.modal.kgImport.progressTitle')}
          size='lg'
          height='lg'
          footer={
            isImportComplete ? (
              <div className='flex justify-end'>
                <Button
                  onClick={() => {
                    dismissNotification(); // Dismiss global indicator FIRST before closing modal
                    setShowImportProgress(false);
                    setImportJobId(null);
                    setIsImportComplete(false);
                    onAgentCreated?.();
                    onClose();
                  }}
                  className='bg-green-600 text-white hover:bg-green-700'
                >
                  <CheckCircleIcon className='mr-2 h-4 w-4' />
                  {t('content.agents.modal.navigation.complete')}
                </Button>
              </div>
            ) : undefined
          }
        >
          <KGImportProgressTracker jobId={importJobId} />
        </Modal>
      )}

      {/* Workflow Editor Overlay */}
      {showWorkflowEditor && (
        <WorkflowEditorOverlay
          isOpen={showWorkflowEditor}
          onClose={() => setShowWorkflowEditor(false)}
          onSave={(definition) => {
            setWorkflowDefinition(definition);
            setShowWorkflowEditor(false);
          }}
          initialDefinition={workflowDefinition}
          languageCode={(formData.languageId === 'en' ? 'en' : 'vi') as SupportedLanguage}
        />
      )}
    </>
  );
};

export default AgentModal;
